import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { avatarColor } from './RecentDonorsBlock';

/**
 * Generic "recent submissions" block for landing pages (Tools). Shows the most
 * recent form fill-ins for a Tool, displaying whichever fields the page author
 * chose (props.displayFieldIds). Self-fetches from the public endpoint
 * GET /api/public/tools/:slug/recent-submissions — which whitelists exactly the
 * fields configured on the page. In the builder it renders sample rows instead.
 *
 * Mirrors RecentDonorsBlock's look, but field-agnostic.
 */

const API_BASE = '/api';

export interface SubmissionFieldMeta { id: string; label: string; type: string; }
export interface SubmissionRow { values: Record<string, string>; submittedAt: string; }

export interface RecentSubmissionsBlockProps {
  props: any;                 // block props: toolSlug, displayFieldIds, title, displayStyle, maxEntries, showTime
  style: any;                 // block style
  baseStyle: React.CSSProperties;
  primary?: string;
  locale?: string;
  /** Slug of the page hosting this block — sent so the endpoint can whitelist
   *  the fields from THIS page's saved block config even when the data source is
   *  another form. Defaults server-side to the data-source slug. */
  pageSlug?: string;
  /** Builder preview → render these sample rows instead of fetching. */
  sample?: { fields: SubmissionFieldMeta[]; submissions: SubmissionRow[] };
}

function timeAgo(iso: string, locale: string): string {
  const d = new Date(iso).getTime();
  if (isNaN(d)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - d) / 1000));
  const he = locale !== 'en';
  if (secs < 60) return he ? 'הרגע' : 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return he ? `לפני ${mins} דקות` : `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return he ? `לפני ${hrs} שעות` : `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return he ? `לפני ${days} ימים` : `${days}d ago`;
}

const RecentSubmissionsBlock: React.FC<RecentSubmissionsBlockProps> = ({ props: p, style: s, baseStyle, primary = '#1565C0', locale = 'he', pageSlug, sample }) => {
  const [data, setData] = useState<{ fields: SubmissionFieldMeta[]; submissions: SubmissionRow[] }>(
    sample || { fields: [], submissions: [] }
  );
  const [loading, setLoading] = useState(!sample);
  const slug: string = p.toolSlug || '';
  const limit = Math.min(50, Math.max(1, Number(p.maxEntries) || 5));

  useEffect(() => {
    if (sample) { setData(sample); setLoading(false); return; }
    if (!slug) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    const pageParam = pageSlug ? `&page=${encodeURIComponent(pageSlug)}` : '';
    axios.get(`${API_BASE}/public/tools/${encodeURIComponent(slug)}/recent-submissions?limit=${limit}${pageParam}`)
      .then(r => { if (!cancelled) setData({ fields: r.data?.fields || [], submissions: r.data?.submissions || [] }); })
      .catch(() => { if (!cancelled) setData({ fields: [], submissions: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug, limit, pageSlug, sample]);

  const dir = locale === 'en' ? 'ltr' : 'rtl';
  const accent = (s as any)?.accentColor || primary;
  const title: string = p.title ?? (locale === 'en' ? 'Recently filled' : 'מילאו לאחרונה');
  const showTime = p.showTime !== false;

  // Field order: the author's chosen order (displayFieldIds), limited to fields
  // the endpoint actually returned. Falls back to all returned fields.
  const metaById = new Map(data.fields.map(f => [f.id, f]));
  const chosen: string[] = Array.isArray(p.displayFieldIds) && p.displayFieldIds.length
    ? p.displayFieldIds.filter((id: string) => metaById.has(id))
    : data.fields.map(f => f.id);

  const displayStyle: 'list' | 'grid' | 'cards' = p.displayStyle || 'list';

  const titleStyle: React.CSSProperties = { fontSize: (s?.fontSize ? Number(s.fontSize) + 2 : 18), fontWeight: 800, color: s?.textColor || '#1f2937', marginBottom: 14, textAlign: 'center' };

  const Avatar: React.FC<{ text: string; size: number }> = ({ text, size }) => {
    const ch = (text.trim()[0] || '?').toUpperCase();
    return (
      <div style={{ width: size, height: size, borderRadius: '50%', background: avatarColor(text.trim() || '?', accent), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: Math.round(size * 0.35), flexShrink: 0 }}>{ch}</div>
    );
  };

  const Row: React.FC<{ r: SubmissionRow }> = ({ r }) => {
    const primaryId = chosen[0];
    const primaryVal = (primaryId ? r.values[primaryId] : '') || '';
    const restIds = chosen.slice(1);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f9fafb', borderRadius: 12, padding: '10px 14px', border: '1px solid #f3f4f6' }}>
        <Avatar text={primaryVal || '?'} size={40} />
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{primaryVal || '—'}</span>
            {showTime && <span style={{ fontSize: 12, color: '#9ca3af', flexShrink: 0 }}>{timeAgo(r.submittedAt, locale)}</span>}
          </div>
          {restIds.map(id => {
            const val = r.values[id];
            if (!val) return null;
            const label = metaById.get(id)?.label || '';
            return (
              <div key={id} style={{ fontSize: 12.5, color: '#4b5563', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {label && <span style={{ color: '#9ca3af' }}>{label}: </span>}{val}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const Card: React.FC<{ r: SubmissionRow }> = ({ r }) => {
    const primaryId = chosen[0];
    const primaryVal = (primaryId ? r.values[primaryId] : '') || '';
    const restIds = chosen.slice(1);
    return (
      <div style={{ background: '#f9fafb', borderRadius: 10, padding: 14, border: '1px solid #f3f4f6', width: 170, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, textAlign: 'center' }}>
        <Avatar text={primaryVal || '?'} size={44} />
        <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{primaryVal || '—'}</div>
        {restIds.map(id => {
          const val = r.values[id];
          if (!val) return null;
          return <div key={id} style={{ fontSize: 12, color: '#4b5563', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</div>;
        })}
        {showTime && <div style={{ fontSize: 12, color: '#9ca3af' }}>{timeAgo(r.submittedAt, locale)}</div>}
      </div>
    );
  };

  const rows = data.submissions;

  return (
    <div dir={dir} style={baseStyle}>
      {title ? <h3 style={titleStyle}>{title}</h3> : null}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '16px 12px', color: '#9ca3af', fontSize: 14 }}>{locale === 'en' ? 'Loading…' : 'טוען…'}</div>
      ) : rows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '16px 12px', color: '#9ca3af', fontSize: 14 }}>
          <span style={{ fontSize: 28, display: 'block', marginBottom: 6 }}>📝</span>
          {locale === 'en' ? 'No submissions yet' : 'עדיין אין רשומות'}
        </div>
      ) : displayStyle === 'cards' ? (
        <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
          {rows.map((r, i) => <Card key={i} r={r} />)}
        </div>
      ) : displayStyle === 'grid' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
          {rows.map((r, i) => <Row key={i} r={r} />)}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map((r, i) => <Row key={i} r={r} />)}
        </div>
      )}
    </div>
  );
};

export default RecentSubmissionsBlock;
